import { IoSearchOutline } from 'react-icons/io5';
import { FiDownload } from 'react-icons/fi';
import React, { useState, useEffect, useContext } from 'react';
import Button from '../../components/shared/custom-btn';
import Heading from '../../components/shared/heading';
import FuelTable from './fuel-table';
import DateRangePicker from '../../components/shared/date-range-picker';
import Modal from '../../components/shared/modal';
import AddModal from './add-modal';
import { AccountsContext } from '../../components/context/account-context';
import { getAllFuels, getAllFuelStatuses } from '../../services/fuels-service';
import { getUsersMinimal } from '../../services/users-service';
import { getVehiclesMinimal } from '../../services/vehicles-service';
import { getAllVendorsMinimal } from '../../services/vendors-service';
import logo from '../../assets/images/logo.jpg';
import { CSVLink } from 'react-csv';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getDateRangeUTC, formatUTCTimestampToLocal } from '../../utils/set-date-range-utils';
import { format } from 'date-fns';
import Help from './help';
import { usePageHelp } from '../../components/context/help-context';

const Fuels = () => {
    usePageHelp(Help);
    const { accountId, attributes } = useContext(AccountsContext);
    const [searchTerm, setSearchTerm] = useState('');
    const [openAddModal, setOpenAddModal] = useState(false);
    const [dateRange, setDateRange] = useState({
        startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
        endDate: new Date(),
    });
    const [newlyAddedItem, setNewlyAddedItem] = useState(null);
    const [fuelData, setFuelData] = useState([]);
    const [allFuelData, setAllFuelData] = useState([]);
    const [error, setError] = useState(null);
    const [users, setUsers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [globalDataloading, setGlobalDataLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState({});

    const fetchGlobalData = async () => {
        if (!accountId) return;
        setGlobalDataLoading(true);

        try {
            const [usersRes, vehiclesRes, vendorsRes, statusOptionRes] = await Promise.all([
                getUsersMinimal(accountId),
                getVehiclesMinimal(accountId),
                getAllVendorsMinimal(accountId),
                getAllFuelStatuses(accountId),
            ]);

            if (usersRes.success) setUsers(usersRes.data.data || []);
            if (vehiclesRes.success) setVehicles(vehiclesRes.data.data || []);
            if (vendorsRes.success) setVendors(vendorsRes.data.data || []);
            if (statusOptionRes.success) setStatusOptions(statusOptionRes.data.data || []);
        } catch (err) {
            console.error("Failed to fetch global data", err);
            setError('Failed to fetch global data');
        } finally {
            setGlobalDataLoading(false);
        }
    };

    const fetchFuels = async () => {
        setLoading(true);
        try {
            const { from, to } = getDateRangeUTC(dateRange);

            const response = await getAllFuels(accountId, from, to);
            console.log(response)

            if (response.success) {
                const sortedData = response.data.data.sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn)); // sort latest first
                setAllFuelData(sortedData);
                setFuelData(sortedData);
                setError(null);
            } else {
                setError(response.data.message || 'Failed to fetch fuel records');
            }
        } catch (err) {
            setError('An error occurred while fetching fuel records');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFuels();
    }, [accountId, dateRange]);

    const handleOpenAddModal = () => {
        setOpenAddModal(true);
    };

    const handleCloseAddModal = () => {
        setOpenAddModal(false);
    };

    const handleAddItem = (newItem) => {
        setNewlyAddedItem(newItem);
        fetchFuels();
    };

    const handleOpenEditModal = (item) => {
        setNewlyAddedItem(item);
    };

    const handleClearNewlyAddedItem = () => {
        setNewlyAddedItem(null);
    };

    // Filter data for export based on selected rows
    const exportData = Object.keys(selectedRows).length > 0
        ? fuelData.filter((_, index) => selectedRows[index])
        : fuelData;

    const csvData = exportData.map(item => ({
        Time: formatUTCTimestampToLocal(item.fuelTime, 'yyyy-MM-dd HH:mm'),
        Vehicle: item.vehicle?.name || '',
        Vendor: item.vendor?.name || 'Unknown',
        Quantity: item.quantity,
        Amount: item.expense?.amount,
        Status: item.status,
        Spender: item.spender?.name || '',
        Odometer: item.odometer,
    }));

    const handleExportPDF = () => {
        const doc = new jsPDF();

        // Logo placement
        const logoWidth = 25;
        const logoHeight = 15;
        const pageWidth = doc.internal.pageSize.getWidth();
        const logoX = (pageWidth - logoWidth) / 2;
        const logoY = 2;

        doc.addImage(logo, 'PNG', logoX, logoY, logoWidth, logoHeight);

        // Title placement
        const title = 'Fuel Report';
        const titleY = logoY + logoHeight + 5;
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        const titleWidth = doc.getTextWidth(title);
        const titleX = (pageWidth - titleWidth) / 2;
        doc.text(title, titleX, titleY);

        // Date range placement
        const start = format(dateRange.startDate, 'yyyy-MM-dd');
        const end = format(dateRange.endDate, 'yyyy-MM-dd');
        const dateRangeText = `Date Range: ${start} to ${end}`;
        const dateRangeY = titleY + 5;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        const dateRangeWidth = doc.getTextWidth(dateRangeText);
        const dateRangeX = (pageWidth - dateRangeWidth) / 2; // Center the date range
        doc.text(dateRangeText, dateRangeX, dateRangeY);

        // Table
        autoTable(doc, {
            head: [['Time', 'Vehicle', 'Vendor', 'Quantity', 'Amount', 'Status', 'Spender', 'Odometer']],
            body: exportData.map(item => [
                formatUTCTimestampToLocal(item.fuelTime, 'yyyy-MM-dd HH:mm'),
                item.vehicle?.name || '',
                item.vendor?.name || 'Unknown',
                item.quantity,
                `$${item.expense?.amount.toFixed(2)}`,
                item.status,
                item.spender?.name || '',
                item.odometer,
            ]),
            startY: dateRangeY + 5, // Start table below date range with 5mm spacing
        });

        // Format filename with date range
        const fileName = `fuels-${start}-to-${end}.pdf`;
        doc.save(fileName);
    };

    const isExportDisabled = Object.keys(selectedRows).length === 0;

    return (
        <div className="px-6 py-4">
            <div className='flex mb-2 items-center justify-between'>
                <div>
                    <Heading>Fuel History</Heading>
                </div>
                <Button
                    label='Add Fuel'
                    onClick={handleOpenAddModal}
                    className='text-white px-4 !py-1 rounded-lg bg-btnBg'
                />
            </div>

            <div className='space-y-4'>
                {error && (
                    <div className="text-red-600 bg-red-100 px-4 py-2 rounded-md">
                        {error}
                    </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex gap-3 flex-wrap">
                        <div className="bg-white rounded-md px-3 flex border border-border items-center gap-2 w-44 lg:w-72 py-1">
                            <IoSearchOutline className="text-gray-500 text-lg" />
                            <input
                                type="text"
                                className="bg-transparent outline-none py-1 w-full text-sm text-gray-700"
                                placeholder="Search"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <DateRangePicker
                            onChange={setDateRange}
                            initialRange={{
                                startDate: dateRange.startDate,
                                endDate: dateRange.endDate,
                            }}
                        />
                    </div>

                    <div className="flex gap-2 items-center">
                        <Button
                            onClick={handleExportPDF}
                            className={` ${isExportDisabled ? '!cursor-not-allowed !opacity-75' : 'hover:!bg-gray-50'} flex items-center gap-1 !text-sm !px-3 !py-1.5 border rounded-md `}
                            label="PDF"
                            icon={<FiDownload className="text-gray-600" />}
                            iconPosition='left'
                            disabled={isExportDisabled}
                        />
                        <div
                            className={`flex items-center gap-1 !text-sm !px-3 !py-1.5 border rounded-md ${isExportDisabled ? 'cursor-not-allowed opacity-75' : 'hover:bg-gray-50'}`}
                        >
                            <CSVLink
                                data={csvData}
                                onClick={(event) => {
                                    if (isExportDisabled) {
                                        event.preventDefault();
                                    }
                                }}
                                filename={`fuels-${format(dateRange.startDate, 'yyyy-MM-dd')}-to-${format(dateRange.endDate, 'yyyy-MM-dd')}.csv`}
                                className={`${isExportDisabled ? 'cursor-not-allowed' : ''} flex items-center gap-1`}
                            >
                                <FiDownload className="text-gray-600" />
                                CSV
                            </CSVLink>
                        </div>
                    </div>
                </div>

                <FuelTable
                    data={fuelData}
                    setData={setFuelData}
                    globalFilter={searchTerm}
                    onGlobalFilterChange={setSearchTerm}
                    newlyAddedItem={newlyAddedItem}
                    setNewlyAddedItem={setNewlyAddedItem}
                    onClearNewlyAddedItem={handleClearNewlyAddedItem}
                    fetchFuels={fetchFuels}
                    loadingData={loading}
                    accountId={accountId}
                    spendors={users}
                    vendors={vendors}
                    vehicles={vehicles}
                    attributes={attributes}
                    statusOptions={statusOptions}
                    fetchGlobalData={fetchGlobalData}
                    globalDataloading={globalDataloading}
                    setSelectedRows={setSelectedRows}
                    selectedRows={selectedRows}
                />

                <Modal
                    isOpen={openAddModal}
                    onClose={handleCloseAddModal}
                    modalClass={'xl:w-[40%] lg:w-[45%] md:w-[65%] sm:w-[70%] w-[90%] !px-0'}
                >
                    <AddModal
                        handleCloseAddModal={handleCloseAddModal}
                        onAdd={handleAddItem}
                        openEditModal={handleOpenEditModal}
                        spendors={users}
                        vendors={vendors}
                        vehicles={vehicles}
                        fetchGlobalData={fetchGlobalData}
                        globalDataloading={globalDataloading}
                    />
                </Modal>
            </div>
        </div>
    );
};

export default Fuels;